import React, { useEffect, useState } from 'react';
import { Box, Popover, Button, Grid, IconButton, Tooltip } from '@mui/material';
import { DateTime } from 'luxon';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import FastRewindRoundedIcon from '@mui/icons-material/FastRewindRounded';
import FastForwardRoundedIcon from '@mui/icons-material/FastForwardRounded';
import { daDK } from '@mui/x-date-pickers/locales';

const formatDate = (date) => {
  const day = date.setLocale('da').toFormat('d.');
  const month = date.setLocale('da').toFormat('MMMM');
  return `${day} ${month}`;
};

// Aligns the new date to the same weekday as the original
const alignToSameWeekday = (date, targetWeekday) => {
  const diff = (date.weekday - targetWeekday + 7) % 7; // Get positive modulo
  return date.minus({ days: diff });
};

const getComparisonPeriod = (date, aggregate, globalDuration) => {
  if (aggregate === 'month') {
    return date.startOf('month').minus({ months: 1 });
  }

  const targetWeekday = date.weekday; // 1–7 (Mon–Sun)
  const target = date.minus({ days: globalDuration });
  return alignToSameWeekday(target, targetWeekday);
};

const getNextComparisonPeriod = (date, aggregate, globalDuration) => {
  if (aggregate === 'month') {
    return date.startOf('month').plus({ months: 1 });
  }

  const targetWeekday = date.weekday;
  const target = date.plus({ days: globalDuration });
  return alignToSameWeekday(target, targetWeekday);
};

const SingleDateSelector = ({
  setComparisonRange,
  globalDuration,
  timeRange,
  isComparingSameNode,
  aggregate,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedDate, setSelectedDate] = useState(
    DateTime.fromMillis(timeRange.start)
      .setZone('Europe/Copenhagen')
      .startOf('day'),
  );

  const canGoForward = () => {
    if (!isComparingSameNode) return false;

    const nextStart =
      aggregate === 'month'
        ? selectedDate.startOf('month').plus({ months: 1 })
        : selectedDate.plus({ days: globalDuration });

    const globalStart = DateTime.fromMillis(timeRange.start)
      .setZone('Europe/Copenhagen')
      .startOf('day');

    return nextStart < globalStart;
  };

  const [selectedDateEnd, setSelectedDateEnd] = useState(
    selectedDate.plus({ days: globalDuration }),
  );

  const allowedWeekday = DateTime.fromMillis(timeRange.start).setZone(
    'Europe/Copenhagen',
  ).weekday; // 1 (Monday) to 7 (Sunday)

  const shouldDisableDate = (date) => {
    return date.weekday !== allowedWeekday;
  };

  const handleDateChange = (date) => {
    if (!date) return;

    const roundedStart = date.startOf('day'); // Ensure full-day selection
    let calculatedStart;
    let calculatedEnd;

    if (isComparingSameNode) {
      calculatedStart = roundedStart;
      calculatedEnd = calculatedStart.plus({ days: globalDuration });
    }

    setSelectedDate(calculatedStart);
    setSelectedDateEnd(calculatedEnd);
    setComparisonRange({
      start: calculatedStart.toUTC().toMillis(),
      end: calculatedEnd.toUTC().toMillis(),
    });

    setAnchorEl(null);
  };

  const handleGoBack = () => {
    const newStart = getComparisonPeriod(
      selectedDate,
      aggregate,
      globalDuration,
    );
    const newEnd =
      aggregate === 'month'
        ? newStart.plus({ months: 1 })
        : newStart.plus({ days: globalDuration });

    setSelectedDate(newStart);
    setSelectedDateEnd(newEnd);
    setComparisonRange({
      start: newStart.toUTC().toMillis(),
      end: newEnd.toUTC().toMillis(),
    });
  };

  const handleGoForward = () => {
    const newStart = getNextComparisonPeriod(
      selectedDate,
      aggregate,
      globalDuration,
    );
    const newEnd =
      aggregate === 'month'
        ? newStart.plus({ months: 1 })
        : newStart.plus({ days: globalDuration });

    setSelectedDate(newStart);
    setSelectedDateEnd(newEnd);
    setComparisonRange({
      start: newStart.toUTC().toMillis(),
      end: newEnd.toUTC().toMillis(),
    });
  };

  useEffect(() => {
    const start = DateTime.fromMillis(timeRange.start)
      .setZone('Europe/Copenhagen')
      .startOf('day');
    let calculatedStart;
    let calculatedEnd;

    if (isComparingSameNode) {
      calculatedStart = getComparisonPeriod(start, aggregate, globalDuration);
      calculatedEnd =
        aggregate === 'month'
          ? calculatedStart.plus({ months: 1 })
          : calculatedStart.plus({ days: globalDuration });
    } else {
      calculatedStart = start;
      calculatedEnd = DateTime.fromMillis(timeRange.end)
        .setZone('Europe/Copenhagen')
        .startOf('day');
    }

    const newRange = {
      start: calculatedStart.toUTC().toMillis(),
      end: calculatedEnd.toUTC().toMillis(),
    };

    setSelectedDate(calculatedStart);
    setSelectedDateEnd(calculatedEnd);
    setComparisonRange((prev) => {
      if (prev.start !== newRange.start || prev.end !== newRange.end) {
        return newRange;
      }
      return prev;
    });
  }, [timeRange, globalDuration, isComparingSameNode, aggregate]);

  return (
    <LocalizationProvider
      dateAdapter={AdapterLuxon}
      adapterLocale="da"
      localeText={
        daDK.components.MuiLocalizationProvider.defaultProps.localeText
      }
    >
      <>
        <Grid container alignItems="center" spacing={2}>
          <Grid item>
            <Tooltip
              title={`Gå ${globalDuration.toFixed(0)} dage tilbage`}
              arrow
            >
              <IconButton
                onClick={handleGoBack}
                disabled={!isComparingSameNode}
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 3,
                  ':hover': { color: 'primary' },
                }}
              >
                <FastRewindRoundedIcon />
              </IconButton>
            </Tooltip>
          </Grid>

          <Grid item>
            <Tooltip title={`Gå ${globalDuration.toFixed(0)} dage frem`} arrow>
              <IconButton
                onClick={handleGoForward}
                disabled={!canGoForward()}
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 3,
                  ':hover': { color: 'primary' },
                }}
              >
                <FastForwardRoundedIcon />
              </IconButton>
            </Tooltip>
          </Grid>

          <Grid item xs>
            <Button
              startIcon={<CalendarTodayRoundedIcon />}
              size="large"
              variant="text"
              disabled={!isComparingSameNode}
              fullWidth
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 3,
                textTransform: 'none',
                color: 'text.primary',
              }}
            >
              {selectedDate
                ? `${formatDate(selectedDate)} - ${formatDate(selectedDateEnd)}`
                : 'Vælg startdato'}
            </Button>
          </Grid>
        </Grid>

        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <StaticDatePicker
            displayStaticWrapperAs="mobile"
            value={selectedDate}
            onChange={handleDateChange}
            maxDate={DateTime.now()}
            shouldDisableDate={shouldDisableDate}
          />
        </Popover>
      </>
    </LocalizationProvider>
  );
};

export default SingleDateSelector;
